import { getTeams } from './endpoints';

/**
 * Cache for team slugs to avoid repeated API calls
 */
let teamSlugsCache: string[] | null = null;

/**
 * Synchronously masks a WordPress URL for a specific team
 * @param url The full WordPress URL
 * @param team Optional team slug for multisite paths
 */
export function getMaskedPathSync(url: string | null | undefined, team?: string): string {
  if (!url) return '';
  
  try {
    // Parse the URL
    const urlObj = new URL(url);
    
    // Check against both production and staging domains
    const wpDomains = ['auslcms.wpenginepowered.com', 'auslcmsstg.wpenginepowered.com'];
    
    // If it's not from our WordPress domains, return the original URL
    if (!wpDomains.some(domain => urlObj.hostname.includes(domain))) {
      return url;
    }
    
    // Get the pathname
    let path = urlObj.pathname;

    // Handle team-specific paths if a team is provided
    if (team) {
      if (path.startsWith(`/${team}/wp-content/`)) {
        path = path.replace(`/${team}/wp-content/`, '/wp-content/');
      } else if (path.startsWith(`/${team}/uploads/`)) {
        path = path.replace(`/${team}/uploads/`, '/uploads/');
      }
    }
    
    return `${path}${urlObj.search}${urlObj.hash}`;
  } catch (e) {
    return url;
  }
}

/**
 * Asynchronously masks a WordPress URL by fetching team slugs first
 * Use this for server-side when team slugs need to be fetched
 * @param url The full WordPress URL
 */
export async function getMaskedPath(url: string | null | undefined): Promise<string> {
  if (!teamSlugsCache) {
    teamSlugsCache = await getTeams();
  }
  // For async version, we'll try to match against all teams
  // This is useful when we don't know which team the URL belongs to
  return getMaskedPathSync(url, teamSlugsCache.find(team => url?.includes(`/${team}/`)));
} 